/**
 * Hauptlayout mit Navigation und Header
 */

import React, { useState } from "react";
import {
  Box,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "./Sidebar";
import Header from "./Header";

interface LayoutProps {
  children: React.ReactNode;
}

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#667eea",
      light: "#8fa4f3",
      dark: "#4c63d2",
    },
    secondary: {
      main: "#764ba2",
      light: "#9575cd",
      dark: "#512da8",
    },
    background: {
      default: "#f5f7fa",
      paper: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          fontWeight: 600,
          padding: "8px 16px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        },
      },
    },
  },
});

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Sidebar state based on screen size
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    // Default: open on desktop, closed on mobile
    return !isMobile;
  });

  if (!isAuthenticated || !user) {
    return null; // Auth-Guard - falls nicht eingeloggt
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <Sidebar
          isOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: "100vh",
            marginLeft: isMobile ? 0 : "280px", // Account for fixed sidebar on desktop
            width: isMobile ? "100vw" : "calc(100vw - 280px)",
            maxWidth: "100vw",
            overflow: "hidden",
            transition: theme.transitions.create(["margin"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          <Header
            user={user}
            onToggleSidebar={
              isMobile ? () => setSidebarOpen(!sidebarOpen) : undefined
            }
          />

          <Box
            sx={{
              flexGrow: 1,
              p: { xs: 0, md: 3 },
              mt: "64px", // Height of AppBar
              backgroundColor: "background.default",
              minHeight: "calc(100vh - 64px)",
              overflow: "hidden",
            }}
          >
            {children}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Layout;
